import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon } from "@hugeicons/core-free-icons"
import { BrandIcon } from '@/components/ui/brand-icons'
import { ALL_CONNECTORS } from '@/lib/config/connectors'
import { cn } from '@/lib/utils'

interface AttributionSetupWizardProps {
  isTrackingActive: boolean
  isRevenueConfigured: boolean
  connectorType: 'stripe' | 'foxy' | null
  metaConnectionCount: number
  basePath: string
  navigate: (path: string) => void
  variant: 'full' | 'compact'
}

type WizardStatus = 'connected' | 'not-connected' | 'coming-soon'

interface WizardRow {
  id: string
  name: string
  description: string
  brandKey: string
  status: WizardStatus
  connectorLabel?: string
}

interface ConnectorRowProps {
  name: string
  description: string
  status: WizardStatus
  icon: React.ReactNode
  onConnect?: () => void
  connectorLabel?: string
}

function ConnectorRow({ name, description, status, icon, onConnect, connectorLabel }: ConnectorRowProps) {
  return (
    <div className={cn(
      "flex items-center gap-3 px-4 py-3",
      status === 'connected' && "bg-success-tint dark:bg-success-tint"
    )}>
      <div className="w-8 h-8 rounded-lg border border-border/30 bg-card flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{name}</span>
          {status === 'connected' && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-live" />
              <span className="text-[11px] text-success dark:text-success font-medium">Connected</span>
            </span>
          )}
          {status === 'coming-soon' && (
            <Badge variant="secondary" className="text-[11px]">Coming soon</Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground/70 mt-0.5 leading-tight">{description}</p>
      </div>
      {status === 'not-connected' && onConnect && (
        <Button variant="outline" size="sm" onClick={onConnect} className="h-7 text-xs shrink-0 gap-1">
          Connect{connectorLabel ? ` ${connectorLabel}` : ''}
        </Button>
      )}
    </div>
  )
}

// Ordered IDs that should appear in the Attribution setup wizard. Picked from
// ALL_CONNECTORS — keeps the wizard in sync with the Connectors page automatically
// (e.g. when a connector flips comingSoon→false, it becomes countable here too).
function buildWizardRows({
  isTrackingActive, isRevenueConfigured, connectorType, metaConnectionCount,
}: Pick<AttributionSetupWizardProps, 'isTrackingActive' | 'isRevenueConfigured' | 'connectorType' | 'metaConnectionCount'>): WizardRow[] {
  const revenueId = connectorType === 'foxy' ? 'foxy' : 'stripe'
  const ids = ['humblytics', revenueId, 'meta', 'google-ads']

  const liveStatus = (id: string): boolean => {
    switch (id) {
      case 'humblytics': return isTrackingActive
      case 'stripe':
      case 'foxy': return isRevenueConfigured
      case 'meta': return metaConnectionCount > 0
      default: return false
    }
  }

  return ids.flatMap((id) => {
    const cfg = ALL_CONNECTORS.find((c) => String(c.id) === id)
    if (!cfg) return []
    const status: WizardStatus = cfg.comingSoon
      ? 'coming-soon'
      : liveStatus(id) ? 'connected' : 'not-connected'
    return [{
      id,
      name: cfg.name,
      description: cfg.description,
      brandKey: cfg.brandKey,
      status,
      connectorLabel: cfg.name,
    }]
  })
}

export default function AttributionSetupWizard({
  isTrackingActive, isRevenueConfigured, connectorType,
  metaConnectionCount, basePath, navigate, variant,
}: AttributionSetupWizardProps) {
  const rows = buildWizardRows({ isTrackingActive, isRevenueConfigured, connectorType, metaConnectionCount })
  // Coming-soon rows render but don't count toward the denominator.
  const countableRows = rows.filter((r) => r.status !== 'coming-soon')
  const connectedCount = countableRows.filter((r) => r.status === 'connected').length
  const totalCount = countableRows.length

  // Compact banner for partial state
  if (variant === 'compact') {
    const missingParts: string[] = []
    if (!isRevenueConfigured) missingParts.push('revenue tracking')
    if (metaConnectionCount === 0) missingParts.push('ad platform')

    if (missingParts.length === 0) return null

    return (
      <div className="rounded-md border border-border/30 bg-muted/20 px-4 py-2.5 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Connect {missingParts.join(' and ')} to unlock{' '}
          {!isRevenueConfigured ? 'revenue attribution' : 'true ROAS and campaign attribution'}.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs shrink-0 gap-1"
          onClick={() => navigate(`${basePath}/connectors`)}
        >
          Set up
          <HugeiconsIcon icon={ArrowRight01Icon} size={12} />
        </Button>
      </div>
    )
  }

  // Full wizard card
  return (
    <Card>
      <CardHeader className="border-b pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold text-foreground">Set up attribution</CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5">Connect your data sources to see verified ROAS</p>
          </div>
          <span className="text-[11px] text-muted-foreground/60">{connectedCount} of {totalCount} connected</span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border/30">
          {rows.map((row) => (
            <ConnectorRow
              key={row.id}
              name={row.name}
              description={row.description}
              status={row.status}
              icon={<BrandIcon brand={row.brandKey as any} className="w-4 h-4" />}
              onConnect={row.status === 'not-connected' ? () => navigate(`${basePath}/connectors`) : undefined}
              connectorLabel={row.connectorLabel}
            />
          ))}
        </div>
        <div className="px-4 py-3 border-t border-border/30 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground/50">{connectedCount} of {totalCount} connected</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`${basePath}/connectors`)}
            className="h-6 text-[11px] text-muted-foreground/60 hover:text-foreground gap-1"
          >
            Manage all connections
            <HugeiconsIcon icon={ArrowRight01Icon} size={10} />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
